// Chandigarh in cut paper (public/site/paper/chandigarh.json, from OpenStreetMap via scripts/build-paper-maps.mjs):
// the Union Territory cut from one sheet, its woods, parks and lakes on top, and the big roads that draw Le Corbusier's
// sector grid. Arriving from the India map it lies flat with north up; scrolling tips it back and turns it until the
// grid lines up with the screen (the city is built 36° off north), the Shivaliks fold up behind it like a pop-up book,
// the street lights come on outwards from Sector 17, and the places in the game rise on their pins, each with a
// postcard: a screenshot from inside the game.
import { useEffect, useRef, useState } from 'react';
import { CHD_STATS } from '../brand';
import { REDUCE, ease, lerp, loadJson, span, useScene } from './scenes';

interface CityData {
  w: number;
  h: number;
  border: string;
  woods: string;
  parks: string;
  water: string;
  roadsA: string;
  roadsB: string;
  rail: string;
  streams: string;
  sectors: { n: string; x: number; y: number }[];
  places: Record<string, [number, number]>;
}

/** the places in the game in detail, with their postcards (screenshots from inside the game) */
export const PLACES = [
  { id: 's17', name: 'Sector 17 Plaza', side: 'r', h: 26, shot: '/site/shots/s17.webp', text: 'Le Corbusier’s city centre: the fountain, the shop blocks, the evening crowd under the arcades.' },
  { id: 'sukhna', name: 'Sukhna Lake', side: 'r', h: 46, shot: '/site/shots/sukhna.webp', text: 'Paddle boats in every colour, the long bund, and the Shivaliks on the horizon.' },
  { id: 'pu', name: 'Panjab University', side: 'r', h: 30, shot: '/site/shots/pu-sc.webp', text: 'The Student Centre’s round drum of 1963 and its ramp spiralling up to the roof, rebuilt from 360° photographs.' },
  { id: 'rosegarden', name: 'Rose Garden underpass', side: 'l', h: 58, shot: '/site/shots/underpass.webp', text: 'The amphitheatre steps, the murals and the tunnel under Jan Marg into the Rose Garden.' },
  { id: 's7', name: 'Sector 7 market', side: 'l', h: 30, shot: null, text: 'The neighbourhood market and the cafés of the Madhya Marg strip, shop by shop.' },
] as const;
/** famous places drawn on the map for bearings (in the city, not yet in detail) */
const BEARINGS: [string, string][] = [
  ['rockgarden', 'Rock Garden'],
  ['leisure', 'Leisure Valley'],
  ['station', 'Railway station'],
];
/** the sector grid runs this many degrees off north */
const GRID = 36;

/** three ridges of the Shivaliks, as the edges of cut paper (seeded, so they're the same every time) */
function ridge(seed: number, base: number, amp: number) {
  let s = seed;
  const r = () => ((s = (s * 16807) % 2147483647) / 2147483647);
  const pts: string[] = [];
  let y = base;
  for (let x = -20; x <= 1020; x += 20) {
    y = base - amp * (0.55 + 0.45 * Math.sin(x / 140 + seed)) - r() * amp * 0.28;
    pts.push(`${x} ${y.toFixed(1)}`);
  }
  return `M-20 200L${pts.join('L')}L1020 200Z`;
}
const RIDGES = [ridge(3, 150, 120), ridge(7, 170, 92), ridge(11, 186, 66)];

export function PaperCity() {
  const sec = useRef<HTMLElement>(null);
  const world = useRef<HTMLDivElement>(null);
  const map = useRef<HTMLDivElement>(null);
  const layers = useRef<(HTMLElement | null)[]>([]);
  const ridges = useRef<(HTMLDivElement | null)[]>([]);
  const pinRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const fade = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<CityData | null>(null);
  const [sel, setSel] = useState(0);
  const [phase, setPhase] = useState(0);
  const chosen = useRef(false);
  const auto = useRef(0);

  useEffect(() => {
    let off = false;
    loadJson<CityData>('/site/paper/chandigarh.json')
      .then((d) => !off && setData(d))
      .catch(() => {});
    return () => {
      off = true;
    };
  }, []);

  useScene(sec, (q) => {
    const w = world.current;
    const m = map.current;
    if (!w || !m || !data) return;
    // still, it holds the city tipped up, lit, with its hills and pins
    const p = REDUCE ? 0.66 : q;
    const arrive = ease.out(span(p, 0, 0.16));
    const open = ease.inOut(span(p, 0.08, 0.42));
    const tilt = lerp(0, 50, open);
    // from north-up to the grid lined up with the screen
    const spin = lerp(-GRID, 0, open);
    const lift = lerp(0, 9, open);
    const scale = lerp(1.35, 1, arrive) * lerp(1, 1.06, open);
    w.style.transform = `rotateX(${tilt.toFixed(2)}deg) rotateZ(${spin.toFixed(2)}deg) scale(${scale.toFixed(4)})`;
    // it fades in under a veil: opacity on the world itself would flatten its 3D (the hills and pins)
    if (fade.current) fade.current.style.opacity = (1 - Math.min(1, arrive * 1.6)).toFixed(3);
    layers.current.forEach((el, i) => {
      if (el) el.style.transform = `translate3d(0, 0, ${(i * lift).toFixed(1)}px)`;
    });
    // the street lights, outwards from Sector 17
    m.style.setProperty('--lights', `${(ease.out(span(p, 0.3, 0.62)) * 115).toFixed(1)}%`);
    // the hills fold up
    ridges.current.forEach((el, i) => {
      if (!el) return;
      const up = ease.land(span(p, 0.3 + i * 0.05, 0.46 + i * 0.05));
      el.style.transform = `translate3d(0, ${(-i * 26).toFixed(1)}px, ${(lift * 6).toFixed(1)}px) rotateX(${(-up * 90).toFixed(2)}deg)`;
      el.style.opacity = Math.min(1, up * 3).toFixed(3);
    });
    const top = layers.current.length * lift + 2;
    pinRefs.current.forEach((el, i) => {
      if (!el) return;
      const rise = ease.out(span(p, 0.42 + i * 0.03, 0.52 + i * 0.03));
      el.style.transform = `translate3d(0, 0, ${top.toFixed(1)}px) rotateZ(${(-(GRID + spin)).toFixed(2)}deg) rotateX(${(-tilt).toFixed(2)}deg) scale(${rise.toFixed(3)})`;
      el.style.opacity = Math.min(1, rise * 1.5).toFixed(3);
      el.style.pointerEvents = rise > 0.9 ? '' : 'none';
    });
    const ph = p < 0.42 ? 0 : 1;
    setPhase((x) => (x === ph ? x : ph));
    // until you pick one, the postcards follow the scroll
    if (!chosen.current) {
      const k = Math.min(PLACES.length - 1, Math.floor(span(p, 0.5, 0.96) * PLACES.length));
      if (k !== auto.current) {
        auto.current = k;
        setSel(k);
      }
    }
  }, 10);

  const at = (id: string) => {
    const q = data?.places[id];
    return q ? { left: `${((q[0] / data!.w) * 100).toFixed(2)}%`, top: `${((q[1] / data!.h) * 100).toFixed(2)}%` } : { display: 'none' };
  };
  const place = PLACES[sel];
  const s17 = data?.places.s17;
  return (
    <section className="pc" id="chandigarh" ref={sec} aria-labelledby="pc-title">
      <div className="pc-stage">
        <div className={`pc-copy${phase ? ' on' : ''}`}>
          <p className="gx-kicker mono">
            <i className="live" /> 02 · First stop · waitlist open
          </p>
          <h2 id="pc-title">
            Chandigarh.
            <br />
            <em>The City Beautiful.</em>
          </h2>
          <p className="dim">Le Corbusier’s planned city, rebuilt at 1:1 from open map data and hundreds of photographs: sector by sector, roundabout by roundabout.</p>
          <dl className="pc-stats">
            <div>
              <dt>buildings</dt>
              <dd>{CHD_STATS.buildings.toLocaleString('en-IN')}</dd>
            </div>
            <div>
              <dt>roads</dt>
              <dd>{CHD_STATS.roads.toLocaleString('en-IN')}</dd>
            </div>
            <div>
              <dt>trees</dt>
              <dd>{CHD_STATS.trees.toLocaleString('en-IN')}</dd>
            </div>
            <div>
              <dt>scale</dt>
              <dd>1:1</dd>
            </div>
          </dl>
        </div>
        <div className="pc-view">
          <div className="pc-world" ref={world}>
            <div className="pc-hills" aria-hidden>
              {RIDGES.map((d, i) => (
                <div key={i} className={`pc-ridge r${i}`} ref={(el) => void (ridges.current[i] = el)}>
                  <svg viewBox="0 0 1000 200" preserveAspectRatio="none">
                    <path d={d} />
                  </svg>
                </div>
              ))}
            </div>
            <div className="pc-map" ref={map} style={s17 && data ? ({ '--lx': `${((s17[0] / data.w) * 100).toFixed(1)}%`, '--ly': `${((s17[1] / data.h) * 100).toFixed(1)}%` } as React.CSSProperties) : undefined}>
              {data && (
                <>
                  {(
                    [
                      ['ground', <path key="b" d={data.border} />],
                      ['woods', <path key="w" d={data.woods} fillRule="evenodd" />],
                      ['parks', <path key="p" d={data.parks} fillRule="evenodd" />],
                      [
                        'water',
                        <g key="wa">
                          <path d={data.water} fillRule="evenodd" />
                          <path className="streams" d={data.streams} />
                        </g>,
                      ],
                      [
                        'roads',
                        <g key="r">
                          <path className="b" d={data.roadsB} />
                          <path className="rail" d={data.rail} />
                          <path className="a" d={data.roadsA} />
                        </g>,
                      ],
                      [
                        'labels',
                        <g key="l">
                          {data.sectors.map((s) => (
                            <text key={s.n} x={s.x} y={s.y} transform={`rotate(${-GRID} ${s.x} ${s.y})`}>
                              {s.n}
                            </text>
                          ))}
                        </g>,
                      ],
                    ] as [string, React.ReactNode][]
                  ).map(([k, el], i) => (
                    <svg key={k} className={`pc-l ${k}`} viewBox={`0 0 ${data.w} ${data.h}`} ref={(x) => void (layers.current[i] = x as unknown as HTMLElement)} aria-hidden>
                      {el}
                    </svg>
                  ))}
                  <div className="pc-pins">
                    {BEARINGS.map(([id, name]) => (
                      <span key={id} className="pc-bear mono" style={at(id)}>
                        {name}
                      </span>
                    ))}
                    {PLACES.map((q, i) => (
                      <button
                        key={q.id}
                        type="button"
                        className={`pc-pin ${q.side}${i === sel ? ' on' : ''}`}
                        style={{ ...at(q.id), ['--h' as string]: `${q.h}px` } as React.CSSProperties}
                        ref={(el) => void (pinRefs.current[i] = el)}
                        onClick={() => {
                          chosen.current = true;
                          setSel(i);
                        }}
                        aria-label={`${q.name}: show its postcard`}
                        aria-pressed={i === sel}
                      >
                        <span className="flag">{q.name}</span>
                        <i className="stick" />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <figure className={`pc-card${phase ? ' on' : ''}`} key={place.id}>
          {place.shot ? <img src={place.shot} alt={`${place.name}, inside the game`} loading="lazy" /> : <div className="blank mono">In the game · no postcard yet</div>}
          <figcaption>
            <b>{place.name}</b>
            <span>{place.text}</span>
          </figcaption>
          <span className="stamp" aria-hidden>
            CHD
            <br />
            1:1
          </span>
        </figure>
        <div className="pc-fade" ref={fade} aria-hidden />
      </div>
    </section>
  );
}
